import { Component } from '@theme/component';

/**
 * @typedef {Object} GalleryRefs
 * @property {HTMLElement} scroller
 * @property {HTMLElement} track
 * @property {HTMLElement} sequence
 */

/** Native horizontal scrolling, with one lightweight animation frame only while visible. @extends {Component<GalleryRefs>} */
class ProjectGallery extends Component {
  requiredRefs = ['scroller', 'track', 'sequence'];
  #abort = new AbortController();
  #intersection;
  #resize;
  #motion = matchMedia('(prefers-reduced-motion: reduce)');
  #reasons = new Set(['outside']);
  #frame = null;
  #navigation = null;
  #last = 0;
  #position = 0;
  #cycle = 0;
  #paused = false;
  #initialized = false;

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#cleanup();
  }

  #cleanup() {
    this.#abort.abort();
    this.#intersection?.disconnect();
    this.#resize?.disconnect();
    if (this.#frame !== null) cancelAnimationFrame(this.#frame);
    this.#frame = null;
    this.#navigation = null;
  }

  #setup() {
    this.#cleanup();
    if (!this.#initialized) {
      this.#paused = this.dataset.autoplay === 'false';
      this.#initialized = true;
    }
    this.#abort = new AbortController();
    this.#reasons = new Set(['outside']);
    if (this.#paused) this.#reasons.add('manual');
    const { signal } = this.#abort;
    const listen = (target, type, handler, options = {}) => target.addEventListener(type, handler, { ...options, signal });
    listen(this.refs.scroller, 'touchstart', () => { this.#navigation = null; this.#pause('touch', true); }, { passive: true });
    const release = () => { if (this.#reasons.has('touch')) this.#pause('touch', false); };
    listen(window, 'touchend', (event) => { if (!event.touches.length) release(); }, { passive: true });
    listen(window, 'touchcancel', release, { passive: true });
    listen(this.refs.scroller, 'keydown', (event) => {
      if (event.code !== 'Space' || event.target !== this.refs.scroller) return;
      event.preventDefault();
      this.#paused = !this.#paused;
      this.#pause('manual', this.#paused);
      const status = this.querySelector('[data-motion-status]');
      if (status) status.textContent = this.#paused ? this.dataset.pausedLabel : this.dataset.playingLabel;
    });
    // Sync native swipe/wheel/keyboard movement without suspending the animation clock.
    listen(this.refs.scroller, 'scroll', () => {
      if (Math.abs(this.refs.scroller.scrollLeft - this.#position) > 2) this.#position = this.refs.scroller.scrollLeft;
    }, { passive: true });
    // Loop copies retain native pointer links, but never enter the keyboard or accessibility tree.
    listen(this.refs.scroller, 'mousedown', (event) => {
      if (event.target instanceof Element && event.target.closest('[data-gallery-clone]')) event.preventDefault();
    });
    listen(document, 'visibilitychange', () => this.#pause('hidden', document.hidden));
    listen(this.#motion, 'change', () => this.#build());
    this.#intersection = new IntersectionObserver(([entry]) => this.#pause('outside', !entry?.isIntersecting), { threshold: 0.05 });
    this.#intersection.observe(this);
    this.#resize = new ResizeObserver(() => this.#build());
    this.#resize.observe(this.refs.scroller);
    this.#build();
  }

  #build() {
    for (const clone of this.refs.track.querySelectorAll('[data-gallery-clone]')) clone.remove();
    this.#cycle = 0;
    if (!this.#motion.matches) {
      const gap = parseFloat(getComputedStyle(this.refs.track).columnGap) || 0;
      const width = this.refs.sequence.getBoundingClientRect().width;
      if (width > 0) {
        this.#cycle = width + gap;
        const count = Math.ceil(this.refs.scroller.clientWidth / this.#cycle);
        for (let index = 0; index < count; index++) {
          const clone = this.refs.sequence.cloneNode(true);
          clone.removeAttribute('ref');
          clone.setAttribute('data-gallery-clone', '');
          clone.setAttribute('aria-hidden', 'true');
          for (const element of clone.querySelectorAll('[id], [ref], [data-shopify-editor-block]')) {
            element.removeAttribute('id'); element.removeAttribute('ref'); element.removeAttribute('data-shopify-editor-block');
          }
          for (const link of clone.querySelectorAll('a, button')) link.setAttribute('tabindex', '-1');
          for (const image of clone.querySelectorAll('img')) image.alt = '';
          this.refs.track.append(clone);
        }
      }
    }
    this.#pause('reduced', this.#motion.matches);
    this.#pause('hidden', document.hidden);
  }

  #pause(reason, active) {
    if (active) this.#navigation = null;
    if (active) this.#reasons.add(reason); else this.#reasons.delete(reason);
    if (this.#frame !== null) cancelAnimationFrame(this.#frame);
    this.#frame = null;
    this.#last = 0;
    this.dataset.motionState = this.#reasons.size || !this.#cycle ? 'paused' : 'playing';
    if (!this.#reasons.size && this.#cycle) {
      this.#position = ((this.refs.scroller.scrollLeft % this.#cycle) + this.#cycle) % this.#cycle;
      this.#frame = requestAnimationFrame(this.#tick);
    }
  }

  #tick = (time) => {
    if (this.#last) {
      const elapsed = Math.min(64, time - this.#last);
      // The Wood Studio hero explicitly opts into faster motion; other galleries retain their limits.
      const maxSpeed = this.classList.contains('ns-photo-gallery--wood-hero') ? 45 : 25;
      const speed = Math.max(18, Math.min(maxSpeed, Number(this.dataset.speed) || 20));
      let navigation = 0;
      if (this.#navigation) {
        navigation = Math.sign(this.#navigation.remaining) * Math.min(Math.abs(this.#navigation.remaining), this.#navigation.speed * elapsed);
        this.#navigation.remaining -= navigation;
        if (Math.abs(this.#navigation.remaining) < .01) this.#navigation = null;
      }
      this.#position = ((this.#position + speed * elapsed / 1000 + navigation) % this.#cycle + this.#cycle) % this.#cycle;
      this.refs.scroller.scrollLeft = this.#position;
    }
    this.#last = time;
    this.#frame = requestAnimationFrame(this.#tick);
  };

  /** Navigation shares the existing animation frame; rapid clicks replace, never queue, the current move. */
  previous() { this.#move(-1); }
  next() { this.#move(1); }
  #move(direction) {
    const card = this.refs.sequence.firstElementChild;
    const gap = parseFloat(getComputedStyle(this.refs.sequence).columnGap) || 0;
    const distance = direction * ((card?.getBoundingClientRect().width || 300) + gap);
    if (this.#reasons.size || !this.#cycle) {
      this.refs.scroller.scrollBy({ left: distance, behavior: 'instant' });
      this.#position = this.refs.scroller.scrollLeft;
      return;
    }
    this.#navigation = { remaining: distance, speed: Math.abs(distance) / 350 };
  }

}

if (!customElements.get('project-gallery')) customElements.define('project-gallery', ProjectGallery);
